import { Injectable } from '@angular/core';

import {
  Action,
  State,
  StateContext,
  StateToken,
  createSelector
} from '@ngxs/store';

import { LoadingStateEnum } from '@app/shared/enums/loading-state.enum';
import { RequestState } from '@app/shared/types/request-state';

import {
  ClearCustomerUIAction,
  SetCustomerUILoadedAction,
  SetCustomerUIErrorAction,
  SetCustomerUILoadingAction
} from './customer-ui.actions';
import { CustomerRequestKey } from './customer.request-keys';

export interface CustomerUIStateModel {
  requestStatus: RequestState;
}

const CUSTOMER_UI_STATE_TOKEN = new StateToken<CustomerUIStateModel>(
  'customerUi'
);

@State<CustomerUIStateModel>({
  name: CUSTOMER_UI_STATE_TOKEN,
  defaults: {
    requestStatus: { errors: [], status: LoadingStateEnum.idle }
  }
})
@Injectable()
export class CustomerUIState {
  static requestStatusByKey(key: CustomerRequestKey) {
    return createSelector(
      [CUSTOMER_UI_STATE_TOKEN],
      (slice: CustomerUIStateModel): RequestState =>
        slice.requestStatus?.key === key
          ? slice.requestStatus
          : { errors: [], status: LoadingStateEnum.idle }
    );
  }

  static isLoadingByKey(key: CustomerRequestKey) {
    return createSelector(
      [CustomerUIState.requestStatusByKey(key)],
      (requestStatus): boolean =>
        requestStatus.status === LoadingStateEnum.loading
    );
  }

  static isLoadedByKey(key: CustomerRequestKey) {
    return createSelector(
      [CustomerUIState.requestStatusByKey(key)],
      (requestStatus): boolean =>
        requestStatus.status === LoadingStateEnum.loaded
    );
  }

  static isErrorByKey(key: CustomerRequestKey) {
    return createSelector(
      [CustomerUIState.requestStatusByKey(key)],
      (requestStatus): boolean =>
        requestStatus.status === LoadingStateEnum.error
    );
  }

  @Action(ClearCustomerUIAction)
  clear(ctx: StateContext<CustomerUIStateModel>) {
    ctx.patchState({
      requestStatus: { errors: [], status: LoadingStateEnum.idle }
    });
  }

  @Action(SetCustomerUILoadingAction)
  setLoading(
    ctx: StateContext<CustomerUIStateModel>,
    { key }: SetCustomerUILoadingAction
  ) {
    ctx.patchState({
      requestStatus: { key, errors: [], status: LoadingStateEnum.loading }
    });
  }

  @Action(SetCustomerUILoadedAction)
  setLoaded(
    ctx: StateContext<CustomerUIStateModel>,
    { key }: SetCustomerUILoadedAction
  ) {
    ctx.patchState({
      requestStatus: { key, errors: [], status: LoadingStateEnum.loaded }
    });
  }

  @Action(SetCustomerUIErrorAction)
  setError(
    ctx: StateContext<CustomerUIStateModel>,
    { key }: SetCustomerUIErrorAction
  ) {
    ctx.patchState({
      requestStatus: { key, errors: [], status: LoadingStateEnum.error }
    });
  }
}
